import { useEffect, useRef, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { t } from 'i18next';
import { MdUpload, RxCross2 } from 'src/icons';

const PDF_MIME_TYPE = 'application/pdf';

// Attempts to actually parse the file as a PDF (catches corrupted PDFs and
// non-PDF files renamed with a .pdf extension), not just check its extension/MIME type.
const isValidPdfContent = async (file: File): Promise<boolean> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfjsLib = await import('pdfjs-dist');
    pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;
    const pdfDocument = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    return pdfDocument.numPages > 0;
  } catch {
    return false;
  }
};

type Props = {
  className?: string;
  // Width classes (e.g. max-w-80) for just the icon + picker button, kept separate from
  // `className` (the whole component incl. error text below) so capping the button's width
  // doesn't also force long error messages to wrap inside that same narrow column.
  pickerClassName?: string;
  inputClassName?: string;
  labelClassName?: string;
  label?: string;
  placeholder?: string;
  name?: string;
  id: string;
  defaultValue?: string;
  register?: Function;
  validation?: object;
  error?: any;
  errorText?: string;
  onChange?: Function;
  onValidatingChange?: (isValidating: boolean) => void;
  hideIcon?: boolean;
  disabled?: boolean;
  allowedFileTypes?: string[];
  maxFileSize?: number; // Size in bytes (e.g., 1MB = 1024 * 1024)
};

const FileUpload = ({
  className = '',
  pickerClassName = '',
  inputClassName = '',
  labelClassName = '',
  label = '',
  placeholder = '',
  id = '',
  name = '',
  defaultValue = '',
  register = () => {},
  onChange = () => {},
  onValidatingChange,
  validation,
  error,
  errorText = 'Please enter a value',
  hideIcon = false,
  disabled = false,
  allowedFileTypes = ['image/png', 'image/jpeg', 'application/pdf'], // Default allowed file types
  maxFileSize = 1024 * 1024, // Default max file size 1MB
}: Props) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null); // State to hold file error
  const [isValidating, setIsValidating] = useState(false);
  const fileUpload = useRef<HTMLInputElement>(null);
  const [isErrorVisible, setIsErrorVisible] = useState(!!error);
  const [fileInputKey, setFileInputKey] = useState(0);
  const validationRequestRef = useRef(0);

  useEffect(() => {
    setIsErrorVisible(!!error);
  }, [error]);

  // Handle file change and validate
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isErrorVisible) {
      setIsErrorVisible(false);
    }
    const file = e.target.files?.[0];
    if (file) {
      // Check file size
      if (file.size > maxFileSize) {
        setFileError(`File size exceeds ${maxFileSize / 1024 / 1024}MB.`);
        setFileName(null);
        return;
      }
      // Check file type
      if (!allowedFileTypes.includes(file.type)) {
        setFileError(
          `Invalid file type. Allowed types: ${allowedFileTypes
            .map((allowedType) => allowedType.split('/')[1]?.toUpperCase() || allowedType)
            .join(', ')}`
        );
        setFileName(null);
        return;
      }

      if (file.type === PDF_MIME_TYPE) {
        // Extension/MIME checks above only look at metadata the browser derives from the
        // file name — they pass for 0-byte files, corrupted PDFs, and non-PDFs simply
        // renamed to .pdf. Actually attempt to parse the file to catch those.
        if (file.size === 0) {
          clearFile(); // resets fileError too, so set the message after
          setFileError(t('file_upload.empty_file'));
          return;
        }

        const requestId = ++validationRequestRef.current;
        setIsValidating(true);
        onValidatingChange?.(true);

        isValidPdfContent(file).then((isValid) => {
          if (validationRequestRef.current !== requestId) return; // superseded by a newer selection
          setIsValidating(false);
          onValidatingChange?.(false);

          if (!isValid) {
            clearFile(); // resets fileError too, so set the message after
            setFileError(t('file_upload.invalid_pdf'));
            return;
          }
          setFileError(null);
          setFileName(file.name);
          if (onChange) onChange(e); // Trigger parent onChange
        });
        return;
      }

      setFileError(null); // Reset error if valid
      setFileName(file.name);
      if (onChange) onChange(e); // Trigger parent onChange
    }
  };

  const clearFile = () => {
    setFileName(null);
    setFileError(null);
    if (fileUpload.current) {
      fileUpload.current.value = ''; // Clear the file input
    }
    setFileInputKey((prev) => prev + 1); // Force remount so same file selection triggers onChange
  };

  return (
    <div className={`grid grid-cols-1 mb-4 relative ${className}`}>
      {label && (
        <label className={`text-xs text-[var(--text-muted)] mb-1 ${labelClassName}`}>
          {label}
        </label>
      )}
      {/* pickerClassName (not className) caps this box's width, so the icon — absolutely
          positioned relative to it — stays aligned with the button's edge without also
          forcing the error text below (a sibling outside this box) into that same narrow column. */}
      <div className={`relative grid grid-cols-1 ${pickerClassName}`}>
        {!hideIcon && (
          <>
            {!fileName ? (
              <MdUpload className="absolute cursor-pointer h-6 w-6 top-3 end-3 text-[var(--text-muted)]"/>
            ) : (
              <RxCross2 className="absolute cursor-pointer h-6 w-6 top-3 end-3 text-[var(--text-muted)]" onClick={clearFile}/>
            )}
          </>
        )}
        <input
          key={fileInputKey}
          className="hidden h-0"
          type="file"
          id={`${id}-filePicker`}
          ref={fileUpload}
          name={name}
          placeholder={placeholder}
          defaultValue={defaultValue}
          accept={allowedFileTypes.join(',')}
          {...register(name, validation)}
          onChange={handleFileChange}
          disabled={disabled || isValidating}
        />
        <label
          htmlFor={`${id}-filePicker`}
          className={`bg-transparent rounded-md text-sm py-3.5 px-3 flex items-center ${inputClassName} ${
            !hideIcon ? 'pe-10' : ''
          } ${isErrorVisible || fileError ? 'border !border-danger' : ''}
            ${disabled || isValidating ? 'cursor-no-drop' : 'cursor-pointer'}`}
        >
          <span className={fileName ? "text-[var(--text-main)]" : "text-[var(--text-muted)] opacity-70"}>
            {isValidating ? t('file_upload.validating') : (fileName || placeholder)}
          </span>
        </label>
      </div>
      {/* Show error message if file is invalid */}
      {fileError && <p className="mt-1 text-xs text-[var(--color-error)]">{fileError}</p>}
      {isErrorVisible && (
        <p className="mt-1 text-xs text-[var(--color-error)]">{errorText}</p>
      )}
      {/* {error && <p className="mt-1 text-xs text-danger">{errorText}</p>} */}
    </div>
  );
};
export default FileUpload;
